import React from 'react';
import { useRouteMatch } from 'react-router';
import { useSelector } from 'hooks/store';
import SectionBasePage from 'components/SectionBasePage';
import { Table } from 'UI';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import { Client } from 'constants/interface';
import { TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { PaginationInternalData } from 'hooks/pagination';
import {
  clientsEntitiesSelector, clientsIdsSelector, isLoadingClientsSelector,
} from 'state/slices/clients';
import { PaymentMethodEnum, PaymentMethodMapping } from 'constants/payment';

import ClientsListExtra from './components/ClientsListExtra';
import {
  MainContainer,
  DetailsButton,
} from './style';

interface ClientListPageProps {
  pageData: PaginationInternalData
  onTableChange: (
    pageData: TablePaginationConfig,
    filterData: Record<string, FilterValue | null>,
    sortData: SorterResult<
    Client> | SorterResult<Client>[],
  ) => void
  onSearch(content: string): void
}

const ClientListPage = ({
  pageData,
  onTableChange,
  onSearch,
}: ClientListPageProps): JSX.Element => {
  const clientsEntities = useSelector(clientsEntitiesSelector);
  const clientsIds = useSelector(clientsIdsSelector);
  const isLoadingClients = useSelector(isLoadingClientsSelector);
  const { url } = useRouteMatch();
  const breadcrumbNavigator = useBreadcrumbNavigation();

  const clients = clientsIds.map((clientId) => {
    const entity = clientsEntities[clientId];
    if (!entity) return undefined;
    const tableClient: Client & {
      key?: string,
    } = {
      ...entity,
      key: clientId.toString(),
    };
    return tableClient;
  }).filter((tableClient): tableClient is Client => !!tableClient);

  const sorter = (): number => 0;

  const paymentMethodFilter = pageData.filter.paymentMethod ?? null;

  return (
    <SectionBasePage title="Clientes" extra={<ClientsListExtra onSearch={onSearch} />}>
      <MainContainer>
        <Table
          dataSource={clients}
          loading={isLoadingClients}
          pagination={pageData}
          showSorterTooltip={false}
          locale={{
            filterReset: 'Limpar',
            cancelSort: 'Clique para limpar ordenação',
            triggerAsc: 'Clique para ordem crescente',
            triggerDesc: 'Clique para ordem decrescente',
          }}
          onChange={onTableChange}
        >
          <Table.Column
            title="Nome"
            key="name"
            dataIndex="name"
            sorter={sorter}
            sortOrder={pageData.orderBy === 'name' ? pageData.order : undefined}
          />
          <Table.Column
            title="Telefone"
            key="phone"
            dataIndex="phone"
            render={(phone: string) => (
              <span>
                { phone ? phone.replace(/(\d{2})((\d{5}\d{4})|(\d{4}\d{4}))/, '($1) $2') : '-'}
              </span>
            )}
          />
          <Table.Column
            title="Adiantamento"
            key="paymentIsUpfront"
            dataIndex="paymentIsUpfront"
            render={(paymentIsUpfront: boolean) => (
              <span>{paymentIsUpfront ? 'Sim' : 'Não'}</span>
            )}
          />
          <Table.Column
            title="Método de pagamento"
            key="paymentMethod"
            dataIndex="paymentMethod"
            render={(paymentMethod: PaymentMethodEnum) => (
              <span>{PaymentMethodMapping[paymentMethod]}</span>
            )}
            sorter={sorter}
            sortOrder={pageData.orderBy === 'paymentMethod' ? pageData.order : undefined}
            filters={Object.values(PaymentMethodEnum).map((paymentMethod) => ({
              text: PaymentMethodMapping[paymentMethod],
              value: paymentMethod,
            }))}
            filterMultiple={false}
            filteredValue={paymentMethodFilter !== null ? [paymentMethodFilter] : null}
          />
          <Table.Column
            title=""
            key="details"
            dataIndex="id"
            render={(clientId) => (
              <DetailsButton onClick={() => breadcrumbNavigator.push({
                label: clientsEntities[clientId]?.name || 'Cliente',
                url: `${url}/${clientId}`,
              })}
              >
                ver detalhes »
              </DetailsButton>
            )}
          />
        </Table>
      </MainContainer>
    </SectionBasePage>
  );
};

export default ClientListPage;
